// apps/dashboard/src/routes/__root.tsx
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useMatches,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { TooltipProvider } from "@/design-system";
import { DashboardShell } from "@/components/chrome/DashboardShell";
import { AuthShell } from "@/components/chrome/AuthShell";
import { PublicShell } from "@/components/chrome/PublicShell";

export interface RouterContext {
  queryClient: QueryClient;
}

type ShellKind = "dashboard" | "auth" | "public";

type ClerkGlobal = { user?: unknown; session?: { status?: string } };

function shellKind(matches: ReturnType<typeof useMatches>): ShellKind {
  for (const m of matches) {
    const shell = (m.staticData as { shell?: ShellKind } | undefined)?.shell;
    if (shell === "auth" || shell === "public") return shell;
  }
  return "dashboard";
}

function isAuthPath(pathname: string): boolean {
  return pathname === "/sign-in" || pathname === "/sign-up";
}

// Unauthenticated demo scan pages live under /public/* and must skip the auth
// guard entirely. Access to the underlying scan is gated server-side by the
// scan's public_available flag — the platform 404s any scan not flagged public.
function isPublicPath(pathname: string): boolean {
  return pathname.startsWith("/public/");
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Router only mounts inside <ClerkLoaded> (see main.tsx), so by the
  // time beforeLoad runs Clerk has finished initialising and `Clerk.user`
  // is the source of truth. No `Clerk.load()` await needed here.
  beforeLoad: ({ location }) => {
    if (isAuthPath(location.pathname) || isPublicPath(location.pathname)) return;
    const clerk = (globalThis as { Clerk?: ClerkGlobal }).Clerk;
    if (!clerk?.user) {
      throw redirect({ to: "/sign-in" });
    }
    // Pending sessions (e.g. Clerk's choose-organization task) are treated
    // as signed-out by the platform's requireAuth, so route them back to
    // /sign-in where Clerk's <SignIn> renders the task picker.
    if (clerk.session?.status === "pending") {
      throw redirect({ to: "/sign-in" });
    }
  },
  component: RootLayout,
});

const SHELLS = {
  auth: AuthShell,
  public: PublicShell,
  dashboard: DashboardShell,
} as const;

function RootLayout() {
  const matches = useMatches();
  const Shell = SHELLS[shellKind(matches)];
  return (
    <TooltipProvider delayDuration={150}>
      <Shell>
        <Outlet />
      </Shell>
    </TooltipProvider>
  );
}
